import { io } from 'socket.io-client';

function normalizeWsBaseUrl(url) {
  let baseUrl = url || '';
  if (typeof baseUrl === 'string' && baseUrl.startsWith('wss://')) {
    baseUrl = baseUrl.replace(/^wss:\/\//, 'https://');
    if (typeof console !== 'undefined' && console.log) console.log('[Proctoring][Widget][WS] normalized wss -> https');
  } else if (typeof baseUrl === 'string' && baseUrl.startsWith('ws://')) {
    baseUrl = baseUrl.replace(/^ws:\/\//, 'http://');
    if (typeof console !== 'undefined' && console.log) console.log('[Proctoring][Widget][WS] normalized ws -> http');
  }
  return baseUrl;
}

class WidgetWebsocketService {
  constructor() {
    this.socket = null;
    this.violationQueue = [];
    this._connectKey = null;
    this._connectListeners = [];
  }

  flushViolationQueue() {
    if (!this.socket || !this.socket.connected) return;
    while (this.violationQueue.length) {
      const payload = this.violationQueue.shift();
      try {
        const preview = payload != null ? (typeof payload === 'object' ? JSON.stringify(payload).slice(0, 200) : String(payload)) : '';
        // eslint-disable-next-line no-console
        console.log('[Proctoring][Widget][WS] SENT violation (queued flush)', preview);
        this.socket.emit('violation', payload);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Proctoring][Widget][WS] violation flush emit failed', err && (err.message || String(err)));
      }
    }
  }

  connect({ url, examId, userId, role }) {
    const baseUrl = normalizeWsBaseUrl(url);
    const connectKey = `${baseUrl}|${examId}|${userId}|${role || ''}`;
    if (this.socket && this._connectKey === connectKey) {
      return this.socket;
    }
    if (this.socket) {
      try { this.socket.disconnect(); } catch (_) {}
      this.socket = null;
    }
    this._connectKey = connectKey;
    const query = {};
    if (role) query.role = role;
    if (examId != null) query.examId = String(examId);
    if (userId != null) query.userId = String(userId);
    // eslint-disable-next-line no-console
    console.log('[Proctoring][Widget][WS] connect', { url: baseUrl, query });
    this.socket = io(baseUrl, {
      query,
      path: '/socket.io',
      reconnection: true,
      reconnectionAttempts: Infinity,
    });
    try {
      this.socket.on('connect', () => {
        // eslint-disable-next-line no-console
        console.log('[Proctoring][Widget][WS] connected', { id: this.socket && this.socket.id, examId: query.examId, userId: query.userId });
        try {
          this.socket.emit('join_exam', { userId: query.userId, examId: query.examId, role: query.role || 'student' });
        } catch (_) { /* ignore */ }
        this.flushViolationQueue();
        this._connectListeners.forEach((fn) => {
          try { fn(); } catch (_) { /* ignore */ }
        });
      });
      this.socket.on('disconnect', (reason) => {
        // eslint-disable-next-line no-console
        console.warn('[Proctoring][Widget][WS] disconnect', { reason });
      });
      this.socket.on('connect_error', (err) => {
        // eslint-disable-next-line no-console
        console.error('[Proctoring][Widget][WS] connect_error', err && (err.message || String(err)));
      });
      this.socket.on('error', (err) => {
        // eslint-disable-next-line no-console
        console.error('[Proctoring][Widget][WS] error', err && (err.message || String(err)));
      });
      this.socket.on('admin_message', (msg) => {
        // eslint-disable-next-line no-console
        console.log('[Proctoring][Widget][WS] admin_message received', msg && (typeof msg === 'object' ? { messageLen: msg.message?.length, userId: msg.userId } : msg));
      });
      this.socket.onAny((event, ...args) => {
        if (event !== 'webcam_frame' && event !== 'screen_frame') {
          // eslint-disable-next-line no-console
          console.log('[Proctoring][Widget][WS] event', event, args && args[0] != null ? (typeof args[0] === 'object' ? JSON.stringify(args[0]).slice(0, 120) : args[0]) : '');
        }
      });
    } catch (_) {}
    return this.socket;
  }

  onConnect(cb) {
    if (typeof cb !== 'function') return () => {};
    if (this.socket && this.socket.connected) {
      try { cb(); } catch (_) { /* ignore */ }
    }
    this._connectListeners.push(cb);
    return () => {
      this._connectListeners = this._connectListeners.filter((fn) => fn !== cb);
    };
  }

  disconnect() {
    this._connectKey = null;
    this._connectListeners = [];
    if (this.socket) {
      try { this.socket.disconnect(); } catch (_) {}
      this.socket = null;
    }
  }

  send(type, payload) {
    if (type === 'violation') {
      if (!this.socket) {
        // eslint-disable-next-line no-console
        console.warn('[Proctoring][Widget][WS] violation dropped (no socket)', payload && payload.clientViolationId);
        return;
      }
      if (!this.socket.connected) {
        this.violationQueue.push(payload);
        // eslint-disable-next-line no-console
        console.warn('[Proctoring][Widget][WS] violation queued until reconnect', {
          clientViolationId: payload && payload.clientViolationId,
          queueLen: this.violationQueue.length
        });
        return;
      }
    } else if (!this.socket) {
      return;
    }
    // eslint-disable-next-line no-console
    const preview = payload != null ? (typeof payload === 'object' ? JSON.stringify(payload).slice(0, 200) : String(payload)) : '';
    console.log('[Proctoring][Widget][WS] SENT', type, preview);
    try {
      this.socket.emit(type, payload);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Proctoring][Widget][WS] emit failed', type, err && (err.message || String(err)));
      if (type === 'violation' && payload) {
        this.violationQueue.push(payload);
      }
    }
  }

  on(type, cb) {
    if (!this.socket) return () => {};
    if (type === '*') {
      const handler = (event, ...args) => cb(event, ...args);
      this.socket.onAny(handler);
      return () => this.socket.offAny(handler);
    }
    this.socket.on(type, cb);
    return () => this.socket.off(type, cb);
  }
}

export default new WidgetWebsocketService();

